import { ChangeDetectionStrategy, Component, inject, input } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ProductEx } from "@front/m19-models";
import { AccountSelectionService, AsinService } from "@front/m19-services";
import { combineLatest, map, switchMap } from "rxjs";
import { ReviewScoreComponent } from "@m19-board/tracking/review-score/review-score.component";

@Component({
  selector: "app-video-asin-preview",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "flex h-full min-w-0 items-start gap-2 w-full",
  },
  template: `
    <div class="bg-muted relative size-28 shrink-0 overflow-hidden rounded-lg border">
      @if (product(); as p) {
        <img class="absolute inset-0 size-full object-cover" [src]="product()?.imageUrl" [alt]="product()?.title" />
      }
    </div>
    <div class="flex w-32 flex-1 shrink-0 flex-col gap-2 py-1.5">
      @if (product()?.brand) {
        <p class="text-muted-foreground truncate">{{ product()?.brand }}</p>
      } @else {
        <div class="bg-muted w-2/3 rounded text-transparent">placeholder</div>
      }
      @if (product()?.title) {
        <p class="line-clamp-2 w-full overflow-hidden">{{ product()?.title }}</p>
      } @else {
        <div class="bg-muted w-1/3 rounded text-transparent">placeholder</div>
      }
      @if (product()?.reviewScore; as reviewScore) {
        <app-review-score [score]="reviewScore" />
      } @else {
        <div class="bg-muted w-1/3 rounded text-transparent">placeholder</div>
      }
    </div>
  `,
  imports: [ReviewScoreComponent],
})
export class VideoAsinPreviewComponent {
  private readonly asinService = inject(AsinService);
  private readonly accountSelection = inject(AccountSelectionService);

  readonly asin = input<string>();

  readonly product = toSignal<ProductEx | undefined>(
    this.accountSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) =>
        combineLatest([this.asinService.getCatalog(am.accountId, am.marketplace), toObservable(this.asin)]),
      ),
      map(([catalog, asin]) => catalog?.asinOffers?.get(asin ?? "") ?? undefined),
    ),
  );
}
